import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { javaApi } from "../api/axios";
import { useEmployees } from "../hooks/useEmployees";
import EmployeeMultiSelect from "./EmployeeMultiSelect";

/* ─── helpers ─────────────────────────────────────────────── */
const getCurrentYearMonth = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

/* ─── BulkGenerateSalaryModal ─────────────────────────────── */
const BulkGenerateSalaryModal = ({ show, onClose }) => {
  const { options: allEmployeeOptions, loading: loadingEmployees } =
    useEmployees({ status: 1 });

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [salaryMonth, setSalaryMonth] = useState(getCurrentYearMonth());
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // id → name lookup built from already-loaded options
  const employeeNameMap = useMemo(() => {
    const map = {};
    allEmployeeOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [allEmployeeOptions]);

  const getEmpName = (id, nameFromApi) =>
    nameFromApi || employeeNameMap[id] || `Emp #${id}`;

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSelectedEmployeeIds([]);
      setSalaryMonth(getCurrentYearMonth());
      setResults(null);
    }
  }, [show]);

  const handleGenerate = async () => {
    if (selectedEmployeeIds.length === 0) {
      toast.error("Please select at least one employee.");
      return;
    }
    if (!salaryMonth) {
      toast.error("Please select a salary month.");
      return;
    }

    setSubmitting(true);
    setResults(null);
    try {
      const res = await javaApi.post("/api/v1/salary/bulk/generate", {
        employeeIds: selectedEmployeeIds,
        salaryMonth,
      });

      const data = res?.data?.data;
      setResults(data);

      if (data?.successCount > 0 && data?.failedCount === 0) {
        toast.success(`Salary generated for ${data.successCount} employee(s).`);
      } else if (data?.successCount > 0 && data?.failedCount > 0) {
        toast.success(`${data.successCount} generated, ${data.failedCount} failed.`);
      } else {
        toast.error("No salary slips were generated.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate salary. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-money-check-dollar me-2 text-primary" />
          Bulk Generate Salary
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── Salary Month ── */}
        <div className="input-block mb-3">
          <label className="col-form-label">
            Salary Month <span className="text-danger">*</span>
          </label>
          <input
            type="month"
            className="form-control"
            value={salaryMonth}
            onChange={(e) => setSalaryMonth(e.target.value)}
            max={getCurrentYearMonth()}
          />
        </div>

        {/* ── Employee Multi-select ── */}
        <div className="input-block mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="col-form-label mb-0">
              Select Employees <span className="text-danger">*</span>
            </label>
            {selectedEmployeeIds.length > 0 && (
              <span className="badge bg-primary">
                {selectedEmployeeIds.length} selected
              </span>
            )}
          </div>
          <EmployeeMultiSelect
            options={allEmployeeOptions}
            selectedValues={selectedEmployeeIds}
            onChange={setSelectedEmployeeIds}
            placeholder={
              loadingEmployees ? "Loading employees..." : "Select Employees..."
            }
            loading={loadingEmployees}
          />
        </div>

        {/* ── Results Summary ── */}
        {results && (
          <div className="mt-3">
            <h6 className="text-muted mb-2">
              Generation Summary — {results.salaryMonth}
            </h6>

            {/* Stat cards row */}
            <div className="row g-2 mb-3">
              {[
                {
                  label: "Requested",
                  value: results.requestedCount,
                  variant: "secondary",
                  icon: "fa-solid fa-list",
                },
                {
                  label: "Generated",
                  value: results.successCount,
                  variant: "success",
                  icon: "fa-solid fa-circle-check",
                },
                {
                  label: "Failed",
                  value: results.failedCount,
                  variant: "danger",
                  icon: "fa-solid fa-circle-xmark",
                },
              ].map((stat) => (
                <div key={stat.label} className="col-4">
                  <div
                    className={`card border-${stat.variant} text-center py-2 px-1`}
                    style={{ borderRadius: 10 }}
                  >
                    <div
                      className={`fs-4 fw-bold text-${stat.variant}`}
                    >
                      {stat.value}
                    </div>
                    <div
                      className="text-muted"
                      style={{ fontSize: 12 }}
                    >
                      <i className={`${stat.icon} me-1`} />
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error list */}
            {results.errors?.length > 0 && (
              <div
                className="p-3"
                style={{
                  background: "#fff5f5",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                }}
              >
                <p
                  className="text-danger fw-semibold mb-2"
                  style={{ fontSize: 13 }}
                >
                  <i className="fa-solid fa-triangle-exclamation me-1" />
                  Errors
                </p>
                {results.errors.map((err, idx) => (
                  <div
                    key={idx}
                    className="d-flex align-items-start gap-2 mb-1"
                    style={{ fontSize: 13 }}
                  >
                    <i
                      className="fa-solid fa-circle-xmark text-danger mt-1"
                      style={{ flexShrink: 0 }}
                    />
                    <span>
                      <strong>{getEmpName(err.employeeId, err.employeeName)}:</strong> {err.message}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Success list */}
            {results.generated?.length > 0 && (
              <div
                className="p-3 mt-2"
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 10,
                }}
              >
                <p
                  className="text-success fw-semibold mb-2"
                  style={{ fontSize: 13 }}
                >
                  <i className="fa-solid fa-circle-check me-1" />
                  Generated ({results.generated.length})
                </p>
                {results.generated.map((item, idx) => (
                  <div
                    key={idx}
                    className="d-flex align-items-center gap-2 mb-1"
                    style={{ fontSize: 13 }}
                  >
                    <i
                      className="fa-solid fa-receipt text-success"
                      style={{ flexShrink: 0 }}
                    />
                    <span>
                      <strong>{getEmpName(item.employeeId, item.employeeName)}</strong> — Salary Generated
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={submitting || selectedEmployeeIds.length === 0 || loadingEmployees}
        >
          {submitting ? (
            <>
              <i className="fa fa-spinner fa-spin me-1" />
              Generating...
            </>
          ) : (
            <>
              <i className="fa-solid fa-bolt me-1" />
              Generate Salary
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkGenerateSalaryModal;
